import React, { useState, useEffect } from 'react';
import { FileText, Building2, User, Calendar, ChevronDown, ChevronUp, Download, Trash2 } from 'lucide-react';
import Layout from '../components/layout/Layout';
import api from '../lib/api';

export default function OnboardingSubmissions() {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => {
    fetchSubmissions();
  }, []);

  const fetchSubmissions = async () => {
    try {
      const res = await api.get('/onboarding/submissions');
      setSubmissions(res.data.submissions || []);
    } catch (err) {
      console.error('Error fetching submissions:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleExpand = async (id) => {
    if (expandedId === id) {
      setExpandedId(null);
      return;
    }
    try {
      const res = await api.get(`/api/onboarding/submissions/${id}`);
      const updated = submissions.map(s => s.id === id ? { ...s, fullData: res.data } : s);
      setSubmissions(updated);
      setExpandedId(id);
    } catch (err) {
      console.error('Error fetching detail:', err);
    }
  };

  const handleExport = (submission) => {
    const data = submission.fullData?.data || {};
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `onboarding_${submission.centro_nome?.replace(/\s+/g, '_') || 'centro'}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Sei sicuro di voler eliminare questa scheda?')) return;
    try {
      await api.delete(`/api/onboarding/submissions/${id}`);
      setSubmissions(submissions.filter(s => s.id !== id));
    } catch (err) {
      console.error('Error deleting:', err);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const d = new Date(dateStr);
    return d.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const statusColors = {
    received: 'bg-amber-100 text-amber-700',
    in_progress: 'bg-blue-100 text-blue-700',
    completed: 'bg-emerald-100 text-emerald-700'
  };

  const statusLabels = {
    received: 'Ricevuta',
    in_progress: 'In lavorazione',
    completed: 'Completata'
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-4 border-sky-500 border-t-transparent rounded-full animate-spin" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="onboarding-submissions-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Schede Onboarding</h1>
            <p className="text-sm text-slate-500 mt-1">Centri medici che hanno compilato il modulo di onboarding</p>
          </div>
          <div className="bg-sky-50 text-sky-700 px-4 py-2 rounded-lg font-medium text-sm">
            {submissions.length} {submissions.length === 1 ? 'scheda ricevuta' : 'schede ricevute'}
          </div>
        </div>

        {submissions.length === 0 ? (
          <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
            <FileText size={48} className="mx-auto text-slate-300 mb-4" />
            <h3 className="text-lg font-medium text-slate-600 mb-2">Nessuna scheda ricevuta</h3>
            <p className="text-sm text-slate-400">Quando un centro medico compilerà il form di onboarding, apparirà qui.</p>
            <p className="text-sm text-slate-400 mt-2">
              Link form: <code className="bg-slate-100 px-2 py-0.5 rounded text-xs">voicebook.telcavoip.ai/onboarding</code>
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {submissions.map((sub) => (
              <div key={sub.id} className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                <div className="flex items-center justify-between p-5 cursor-pointer hover:bg-slate-50 transition-colors"
                  onClick={() => handleExpand(sub.id)} data-testid={`submission-${sub.id}`}>
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 bg-sky-100 rounded-lg flex items-center justify-center">
                      <Building2 size={20} className="text-sky-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-800">{sub.centro_nome || 'Centro senza nome'}</h3>
                      <div className="flex items-center gap-3 mt-0.5">
                        <span className="text-sm text-slate-400">{sub.referente_email || 'Nessun email'}</span>
                        <span className="text-slate-300">|</span>
                        <span className="text-sm text-slate-400">{formatDate(sub.submitted_at)}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${statusColors[sub.status] || statusColors.received}`}>
                      {statusLabels[sub.status] || 'Ricevuta'}
                    </span>
                    {expandedId === sub.id ? <ChevronUp size={18} className="text-slate-400" /> : <ChevronDown size={18} className="text-slate-400" />}
                  </div>
                </div>

                {expandedId === sub.id && sub.fullData && (
                  <div className="border-t border-slate-100 p-5 bg-slate-50">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
                      {/* Centro Info */}
                      <div className="bg-white rounded-lg p-4 border border-slate-100">
                        <h4 className="font-medium text-slate-700 mb-2 flex items-center gap-2">
                          <Building2 size={16} className="text-sky-500" /> Centro
                        </h4>
                        <div className="space-y-1 text-sm text-slate-600">
                          <p><strong>Nome:</strong> {sub.fullData.data?.centro?.nome}</p>
                          <p><strong>Indirizzo:</strong> {sub.fullData.data?.centro?.indirizzo}, {sub.fullData.data?.centro?.citta}</p>
                          <p><strong>Telefono:</strong> {sub.fullData.data?.centro?.telefono}</p>
                          <p><strong>Email:</strong> {sub.fullData.data?.centro?.email}</p>
                          <p><strong>P.IVA:</strong> {sub.fullData.data?.centro?.partita_iva}</p>
                          <p><strong>Orari:</strong> {sub.fullData.data?.centro?.orario_apertura} - {sub.fullData.data?.centro?.orario_chiusura}</p>
                          <p><strong>Giorni:</strong> {sub.fullData.data?.centro?.giorni_apertura?.join(', ')}</p>
                        </div>
                      </div>

                      {/* Referente */}
                      <div className="bg-white rounded-lg p-4 border border-slate-100">
                        <h4 className="font-medium text-slate-700 mb-2 flex items-center gap-2">
                          <User size={16} className="text-amber-500" /> Referente
                        </h4>
                        <div className="space-y-1 text-sm text-slate-600">
                          <p><strong>Nome:</strong> {sub.fullData.data?.centro?.referente_nome}</p>
                          <p><strong>Ruolo:</strong> {sub.fullData.data?.centro?.referente_ruolo}</p>
                          <p><strong>Tel:</strong> {sub.fullData.data?.centro?.referente_telefono}</p>
                          <p><strong>Email:</strong> {sub.fullData.data?.centro?.referente_email}</p>
                        </div>
                      </div>

                      {/* Reparti */}
                      <div className="bg-white rounded-lg p-4 border border-slate-100">
                        <h4 className="font-medium text-slate-700 mb-2 flex items-center gap-2">
                          <FileText size={16} className="text-violet-500" /> Reparti ({sub.fullData.data?.reparti?.length || 0})
                        </h4>
                        <div className="space-y-2 text-sm text-slate-600">
                          {sub.fullData.data?.reparti?.map((r, i) => (
                            <div key={i}>
                              <p className="font-medium">{r.nome || `Reparto ${i+1}`}</p>
                              <p className="text-xs text-slate-400">
                                {r.servizi?.filter(s => s).join(', ') || 'Nessun servizio'}
                                {' '} — Cal. {r.calendario_unificato ? 'unificato' : 'separato'}
                              </p>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Medici */}
                      <div className="bg-white rounded-lg p-4 border border-slate-100">
                        <h4 className="font-medium text-slate-700 mb-2 flex items-center gap-2">
                          <User size={16} className="text-blue-500" /> Medici ({sub.fullData.data?.medici?.length || 0})
                        </h4>
                        <div className="space-y-1 text-sm text-slate-600">
                          {sub.fullData.data?.medici?.map((m, i) => (
                            <p key={i}>Dr. {m.nome} {m.cognome} — {m.specializzazione || m.reparto}</p>
                          ))}
                        </div>
                      </div>

                      {/* Disponibilità */}
                      <div className="bg-white rounded-lg p-4 border border-slate-100">
                        <h4 className="font-medium text-slate-700 mb-2 flex items-center gap-2">
                          <Calendar size={16} className="text-teal-500" /> Disponibilità
                        </h4>
                        <div className="space-y-1 text-sm text-slate-600">
                          {sub.fullData.data?.disponibilita?.map((d, i) => {
                            const medico = sub.fullData.data?.medici?.[d.medico_index];
                            return (
                              <div key={i}>
                                <p className="font-medium">{medico ? `Dr. ${medico.nome} ${medico.cognome}` : `Config ${i+1}`}</p>
                                <p className="text-xs text-slate-400">
                                  {d.giorni?.map(g => g.slice(0,3)).join(', ')} | {d.orario_inizio}-{d.orario_fine} | Slot: {d.durata_slot}
                                </p>
                              </div>
                            );
                          })}
                        </div>
                      </div>

                      {/* Config */}
                      <div className="bg-white rounded-lg p-4 border border-slate-100">
                        <h4 className="font-medium text-slate-700 mb-2 flex items-center gap-2">
                          <FileText size={16} className="text-rose-500" /> Configurazione
                        </h4>
                        <div className="space-y-1 text-sm text-slate-600">
                          <p>SMS: {sub.fullData.data?.config?.promemoria_sms ? 'Sì' : 'No'} | Email: {sub.fullData.data?.config?.promemoria_email ? 'Sì' : 'No'}</p>
                          <p>Promemoria: {sub.fullData.data?.config?.ore_prima_promemoria}h prima</p>
                          <p>Lista attesa: {sub.fullData.data?.config?.lista_attesa_attiva ? 'Sì' : 'No'}</p>
                          <p>Max prenotazioni: {sub.fullData.data?.config?.max_prenotazioni_giorno_medico}/giorno</p>
                        </div>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex gap-3 pt-3 border-t border-slate-200">
                      <button onClick={() => handleExport(sub)}
                        className="flex items-center gap-2 px-4 py-2 bg-sky-100 text-sky-700 rounded-lg text-sm font-medium hover:bg-sky-200 transition-colors">
                        <Download size={16} /> Esporta JSON
                      </button>
                      <button onClick={() => handleDelete(sub.id)}
                        className="flex items-center gap-2 px-4 py-2 bg-red-50 text-red-600 rounded-lg text-sm font-medium hover:bg-red-100 transition-colors">
                        <Trash2 size={16} /> Elimina
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
